import { TableOutlined, LineChartOutlined } from '@ant-design/icons';
import { Button, Card, Table, Tooltip as AntTooltip, Typography } from 'antd';
import { useState } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  TooltipProps,
  XAxis,
  YAxis,
} from 'recharts';
import { IBucket } from './kpiSeries';

const { Text } = Typography;

/**
 * 系列色。dataviz の参照パレットの先頭3色（青・橙・水色）で、この3色は全ペアで
 * 色覚多様性の判別基準を満たす。4系列目は黄と橙が並んで判別できなくなるので、
 * 1グラフ3系列までにしている。水色は白地で 3:1 を下回るため凡例と表表示を必ず付ける。
 */
export const SERIES_COLORS = ['#2a78d6', '#eb6834', '#1baf7a'];
const INK_MUTED = '#666666';
const GRID = '#E5E5E5';

export interface ISeries {
  id: string;
  label: string;
}

interface IProps {
  title: string;
  /** 指標の定義や注意書き（タイトル下に小さく出す） */
  note?: string;
  kind: 'line' | 'bar';
  buckets: IBucket[];
  series: ISeries[];
  unit?: string;
}

export const formatNumber = (v: number | null | undefined, unit = ''): string =>
  typeof v === 'number' ? `${v.toLocaleString('ja-JP')}${unit}` : '—';

const periodText = (b: IBucket): string => {
  const range = b.from === b.to ? b.from : `${b.from} 〜 ${b.to}`;
  if (b.days === 0) return `${range}（集計なし）`;

  return b.days < b.expectedDays ? `${range}（${b.expectedDays}日中 ${b.days}日分）` : range;
};

const ChartTooltip: React.FC<TooltipProps<number, string> & { unit?: string; series: ISeries[] }> = ({
  active,
  payload,
  unit,
  series,
}) => {
  if (!active || !payload?.length) return null;
  const bucket = payload[0].payload as IBucket;

  return (
    <div className='bg-white border border-line-subtle rounded-md px-md py-sm shadow-sm text-[12px]'>
      <div className='text-fg-muted mb-xs'>{periodText(bucket)}</div>
      {series.map((s, i) => (
        <div key={s.id} className='flex items-center gap-sm'>
          <span
            className='inline-block w-[8px] h-[8px] rounded-full'
            style={{ background: SERIES_COLORS[i] }}
          />
          <span className='text-fg-muted'>{s.label}</span>
          <span className='ml-auto pl-md text-fg font-semibold'>
            {formatNumber(bucket.values[s.id], unit)}
          </span>
        </div>
      ))}
    </div>
  );
};

/**
 * 折れ線の点。前後が欠けた孤立点は線にならず見えないので、その点だけマーカーを出す
 * （connectNulls={false} で欠けた日を途切れさせているため）
 */
export const isolatedDot =
  (buckets: IBucket[], id: string, color: string) =>
  (props: { cx?: number; cy?: number; index?: number }) => {
    const at = (j: number) => buckets[j]?.values[id];
    const idx = props.index ?? -1;
    const isolated =
      typeof at(idx) === 'number' &&
      typeof at(idx - 1) !== 'number' &&
      typeof at(idx + 1) !== 'number';

    return isolated && props.cx != null && props.cy != null ? (
      <circle
        key={`${id}-${idx}`}
        cx={props.cx}
        cy={props.cy}
        r={4}
        fill={color}
        stroke='#fff'
        strokeWidth={2}
      />
    ) : (
      <g key={`${id}-${idx}`} />
    );
  };

export const KpiChart: React.FC<IProps> = ({ title, note, kind, buckets, series, unit }) => {
  const [asTable, setAsTable] = useState(false);
  const data = buckets.map((b) => ({ ...b, ...b.values }));
  const showLegend = series.length >= 2;

  const axes = (
    <>
      <CartesianGrid stroke={GRID} vertical={false} />
      <XAxis
        dataKey='label'
        tick={{ fill: INK_MUTED, fontSize: 11 }}
        tickLine={false}
        axisLine={{ stroke: GRID }}
        minTickGap={16}
      />
      <YAxis
        tick={{ fill: INK_MUTED, fontSize: 11 }}
        tickLine={false}
        axisLine={false}
        width={56}
        tickFormatter={(v: number) => v.toLocaleString('ja-JP')}
        allowDecimals={!!unit}
      />
      <Tooltip
        content={<ChartTooltip unit={unit} series={series} />}
        cursor={kind === 'line' ? { stroke: INK_MUTED, strokeDasharray: '3 3' } : { fill: '#F4F4F4' }}
      />
      {showLegend && (
        <Legend
          verticalAlign='top'
          align='right'
          iconType='circle'
          iconSize={8}
          height={24}
          formatter={(value: string) => <span className='text-fg text-[12px]'>{value}</span>}
        />
      )}
    </>
  );

  return (
    <Card
      size='small'
      title={
        <div className='py-xs'>
          <div className='text-[14px]'>{title}</div>
          {note && (
            <Text type='secondary' className='text-[12px] font-normal whitespace-normal'>
              {note}
            </Text>
          )}
        </div>
      }
      extra={
        <AntTooltip title={asTable ? 'グラフで見る' : '表で見る'}>
          <Button
            type='text'
            size='small'
            icon={asTable ? <LineChartOutlined /> : <TableOutlined />}
            onClick={() => setAsTable((v) => !v)}
            aria-label={asTable ? 'グラフで見る' : '表で見る'}
          />
        </AntTooltip>
      }
    >
      {asTable ? (
        <Table
          size='small'
          rowKey='from'
          pagination={false}
          scroll={{ y: 240 }}
          dataSource={[...buckets].reverse()}
          columns={[
            { title: '期間', key: 'period', render: (_: unknown, b: IBucket) => periodText(b) },
            ...series.map((s) => ({
              title: s.label,
              key: s.id,
              align: 'right' as const,
              render: (_: unknown, b: IBucket) => formatNumber(b.values[s.id], unit),
            })),
          ]}
        />
      ) : (
        <div style={{ height: 240 }}>
          <ResponsiveContainer width='100%' height='100%'>
            {kind === 'line' ? (
              <LineChart data={data} margin={{ top: 4, right: 8, bottom: 0, left: 0 }}>
                {axes}
                {series.map((s, i) => (
                  <Line
                    key={s.id}
                    type='monotone'
                    dataKey={s.id}
                    name={s.label}
                    stroke={SERIES_COLORS[i]}
                    strokeWidth={2}
                    dot={isolatedDot(buckets, s.id, SERIES_COLORS[i])}
                    activeDot={{ r: 4, stroke: '#fff', strokeWidth: 2 }}
                    connectNulls={false}
                    isAnimationActive={false}
                  />
                ))}
              </LineChart>
            ) : (
              <BarChart data={data} margin={{ top: 4, right: 8, bottom: 0, left: 0 }} barGap={2}>
                {axes}
                {series.map((s, i) => (
                  <Bar
                    key={s.id}
                    dataKey={s.id}
                    name={s.label}
                    fill={SERIES_COLORS[i]}
                    radius={[4, 4, 0, 0]}
                    maxBarSize={24}
                    isAnimationActive={false}
                  />
                ))}
              </BarChart>
            )}
          </ResponsiveContainer>
        </div>
      )}
    </Card>
  );
};
